import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../data/cakes';
import './Cart.css';

const Cart = () => {
  const { cart, updateQuantity, updateWeight, removeItem, getTotals } = useCart();
  const navigate = useNavigate();
  const totals = getTotals();

  const handleQuantityChange = (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
  };

  const handleWeightChange = (itemId, newWeight) => {
    updateWeight(itemId, parseFloat(newWeight));
  };

  const handleRemoveItem = (itemId) => {
    if (window.confirm('Remove this item from cart?')) {
      removeItem(itemId);
    }
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) return;
    navigate('/checkout');
  };

  if (cart.items.length === 0) {
    return (
      <div className="cart-page">
        <header className="cart-header glass-effect">
          <div className="cart-header-content">
            <Link to="/" className="back-button ripple">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </Link>
            <h1 className="cart-title">Shopping Cart</h1>
          </div>
        </header>

        <div className="empty-cart fade-in">
          <div className="empty-cart-icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some delicious cakes to get started!</p>
          <Link to="/" className="btn btn-primary">
            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
            </svg>
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <header className="cart-header glass-effect">
        <div className="cart-header-content">
          <Link to="/" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="cart-title">Shopping Cart</h1>
          <div className="cart-count-badge">{cart.items.length}</div>
        </div>
      </header>

      <div className="cart-content">
        <div className="container">
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items-section">
              <div className="cart-items">
                {cart.items.map((item, index) => (
                  <div key={item.id} className="cart-item card fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="cart-item-image">
                      {item.cakeImage ? (
                        <img src={getImageUrl(item.cakeImage)} alt={item.cakeTitle} />
                      ) : (
                        <div className="cart-item-placeholder gradient-secondary">
                          <span>🎂</span>
                        </div>
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h3 className="cart-item-title">{item.cakeTitle}</h3>
                      <div className="cart-item-meta">
                        <div className="meta-badge weight-selector">
                          <svg className="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
                          </svg>
                          <select 
                            className="weight-select"
                            value={item.weight}
                            onChange={(e) => handleWeightChange(item.id, e.target.value)}
                          >
                            {(() => {
                              const options = [];
                              const baseWeight = item.basePrice ? 0.5 : 0.5; // Start from 0.5kg
                              for (let w = baseWeight; w <= 10; w += 0.5) {
                                options.push(
                                  <option key={w} value={w}>{w.toFixed(1)}kg</option>
                                );
                              }
                              return options;
                            })()}
                          </select>
                        </div>
                        <span className="meta-badge">
                          <svg className="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                          </svg>
                          {item.flavourName}
                        </span>
                      </div>
                      <div className="cart-item-price">₹{item.unitPrice.toLocaleString()}</div>
                    </div>

                    <div className="cart-item-actions">
                      <div className="quantity-control">
                        <button 
                          className="quantity-btn ripple"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path>
                          </svg>
                        </button>
                        <span className="quantity-value">{item.quantity}</span>
                        <button 
                          className="quantity-btn ripple"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                          </svg>
                        </button>
                      </div>

                      <button 
                        className="remove-btn ripple"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>

                      <div className="cart-item-subtotal">
                        ₹{item.subtotal.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary-section">
              <div className="order-summary card">
                <h2 className="summary-title">Order Summary</h2>
                
                <div className="summary-rows">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span className="summary-value">₹{totals.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax (5%)</span>
                    <span className="summary-value">₹{totals.tax.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Delivery Fee</span>
                    <span className="summary-value">₹{totals.deliveryFee}</span>
                  </div>
                  <div className="summary-total">
                    <span>Total</span>
                    <span className="total-value">₹{totals.total.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  className="checkout-btn btn-primary ripple"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
